import axios from 'axios';
import { toast } from 'react-toastify';
import { resetLowStockAlertDismissal } from '../utils/lowStockAlert';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Handle 401 Unauthorized
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        resetLowStockAlertDismissal();
        window.location.href = '/login';
        toast.error('Session expired. Please login again.');
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('An error occurred');
      }
    } else {
      toast.error('Network error. Please check your connection.');
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  logout: () => api.post('/auth/logout')
};

// Users APIs
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updateStatus: (id, status) => api.put(`/users/${id}/status`, { status })
};

// Dashboard APIs
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getLocationStock: () => api.get('/dashboard/location-stock'),
  getRecentInvoices: () => api.get('/dashboard/recent-invoices'),
  getCategoryStock: () => api.get('/dashboard/category-stock'),
  getLowStock: () => api.get('/dashboard/low-stock'),
  getStockMovement: (days) => api.get('/dashboard/stock-movement', { params: { days } })
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  uploadLogo: (formData) => api.post('/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteLogo: () => api.delete('/settings/logo'),
  uploadSignature: (formData) => api.post('/settings/signature', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteSignature: () => api.delete('/settings/signature')
};

// Categories APIs
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getActive: () => api.get('/categories/active'),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  updateStatus: (id, status) => api.put(`/categories/${id}/status`, { status })
};

// Sub-Categories APIs
export const subCategoriesAPI = {
  getAll: (params) => api.get('/sub-categories', { params }),
  getByCategory: (categoryId) => api.get(`/sub-categories/category/${categoryId}`),
  getById: (id) => api.get(`/sub-categories/${id}`),
  create: (data) => api.post('/sub-categories', data),
  update: (id, data) => api.put(`/sub-categories/${id}`, data),
  delete: (id) => api.delete(`/sub-categories/${id}`)
};

// Products APIs
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getLowStock: () => api.get('/products/low-stock'),
  getStockAlert: (threshold) => api.get('/products/stock-alert', { params: { threshold } }),
  getById: (id) => api.get(`/products/${id}`),
  getStockHistory: (id, params) => api.get(`/products/${id}/stock-history`, { params }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  uploadPhoto: (id, formData) => api.post(`/products/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deletePhoto: (id) => api.delete(`/products/${id}/photo`)
};

// Stock-In APIs
export const stockInAPI = {
  getAll: (params) => api.get('/stock/in', { params }),
  getById: (id) => api.get(`/stock/in/${id}`),
  create: (data) => api.post('/stock/in', data),
  update: (id, data) => api.put(`/stock/in/${id}`, data),
  delete: (id) => api.delete(`/stock/in/${id}`)
};

// Stock-Out APIs
export const stockOutAPI = {
  getAll: (params) => api.get('/stock/out', { params }),
  getById: (id) => api.get(`/stock/out/${id}`),
  create: (data) => api.post('/stock/out', data),
  update: (id, data) => api.put(`/stock/out/${id}`, data),
  delete: (id) => api.delete(`/stock/out/${id}`),
  getStates: () => api.get('/stock/states')
};

// Invoices APIs
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  getStockOutAsInvoice: (id) => api.get(`/invoices/stockout/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  updatePayment: (id, data) => api.put(`/invoices/${id}/payment`, data),
  uploadSignature: (id, formData) => api.post(`/invoices/${id}/signature`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteSignature: (id) => api.delete(`/invoices/${id}/signature`)
};

// Customers APIs
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getAllForDropdown: () => api.get('/customers/all'),
  search: (query) => api.get('/customers/search', { params: { q: query } }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  lookupGstin: (gstin) => api.get(`/customers/gstin/${gstin}`)
};

// Print Services APIs
export const printServicesAPI = {
  getAll: (params) => api.get('/print-services', { params }),
  getById: (id) => api.get(`/print-services/${id}`),
  create: (data) => api.post('/print-services', data),
  update: (id, data) => api.put(`/print-services/${id}`, data),
  delete: (id) => api.delete(`/print-services/${id}`),
  updateStatus: (id, status) => api.put(`/print-services/${id}/status`, { status })
};

// Purchase Bills APIs
export const purchaseBillsAPI = {
  getAll: (params) => api.get('/purchase-bills', { params }),
  getById: (id) => api.get(`/purchase-bills/${id}`),
  create: (data) => api.post('/purchase-bills', data),
  update: (id, data) => api.put(`/purchase-bills/${id}`, data),
  delete: (id) => api.delete(`/purchase-bills/${id}`),
  uploadImage: (id, formData) => api.post(`/purchase-bills/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Suppliers APIs (independent from customers)
export const suppliersAPI = {
  getAll: (params) => api.get('/suppliers', { params }),
  getAllForDropdown: () => api.get('/suppliers/all'),
  search: (query) => api.get('/suppliers/search', { params: { q: query } }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  lookupGstin: (gstin) => api.get(`/suppliers/gstin/${gstin}`)
};

// Printing/Branding APIs
export const printingBrandingAPI = {
  getAll: (params) => api.get('/printing-branding', { params }),
  getById: (id) => api.get(`/printing-branding/${id}`),
  create: (data) => api.post('/printing-branding', data),
  update: (id, data) => api.put(`/printing-branding/${id}`, data),
  delete: (id) => api.delete(`/printing-branding/${id}`),
  getItemSuggestions: (query) => api.get('/printing-branding/item-suggestions', { params: { q: query } })
};

// Reports APIs
export const reportsAPI = {
  getStockSummary: (params) => api.get('/reports/stock-summary', { params }),
  getProductMovement: (params) => api.get('/reports/product-movement', { params }),
  getInvoiceHistory: (params) => api.get('/reports/invoice-history', { params }),
  getLowStock: (params) => api.get('/reports/low-stock', { params }),
  getSalesSummary: (params) => api.get('/reports/sales-summary', { params }),
  getLocationStock: (params) => api.get('/reports/location-stock', { params }),
  getPartyLedger: (params) => api.get('/reports/party-ledger', { params }),
  getPurchaseBills: (params) => api.get('/reports/purchase-bills', { params })
};

// Roles APIs
export const rolesAPI = {
  getAll: (params) => api.get('/roles', { params }),
  getActive: () => api.get('/roles/active'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  updateStatus: (id, status) => api.put(`/roles/${id}/status`, { status })
};

export default api;
